// Client-only PostHog loader (contract C2). Import it ONLY from
// src/entry-client.js. It holds what boot needs: the gate, the facade queue
// handling, the consent listeners, the landing snapshot and the page type.
// posthog-js (slim build) and posthogRuntime.ts (config, before_send
// sanitizer, adapter, landing campaign) arrive through dynamic import()s once
// the storefront is ready, so neither reaches the SSR bundle or the entry
// chunk. Everything else talks to PostHog through the SSR-safe facade in
// productAnalytics.ts (window.__vfPh / __vfPhQueue).
import { CONSENT_STORAGE_KEY } from "../privacy/consent";
import { classifyNavigation } from "../utils/navigationTransition";
import { isAutomatedBrowser } from "../utils/webVitals";
import type {
  PostHogClient,
  PostHogLanding,
  PostHogRuntime,
  startPostHog,
} from "./posthogRuntime";

declare global {
  interface Window {
    __vfPhLoaderInstalled?: boolean;
  }
}

type PostHogModule = { default: PostHogClient };
type PostHogRuntimeModule = { startPostHog: typeof startPostHog };

export type PostHogLoaderOptions = {
  // Tests only. The storefront always uses the build mode, the real SDK and
  // the real runtime module.
  production?: boolean;
  loadSdk?: () => Promise<PostHogModule>;
  loadRuntime?: () => Promise<PostHogRuntimeModule>;
};

export const POSTHOG_TOKEN_PATTERN = /^phc_[A-Za-z0-9]+$/;

const loadSlimSdk = () => import("posthog-js/dist/module.slim.js");
const loadPostHogRuntime = () => import("./posthogRuntime");

// ── Page type ───────────────────────────────────────────────────────────────

export type PostHogPageType =
  | "home"
  | "category"
  | "brand"
  | "product"
  | "search"
  | "cart"
  | "checkout"
  | "success"
  | "account"
  | "info"
  | "other";

type ResolvedSeoRoute = {
  type?: unknown;
  path?: unknown;
  notFound?: unknown;
} | null;

const normalizePath = (value: string): string => {
  const path = String(value || "/").split(/[?#]/, 1)[0] || "/";
  return path === "/" ? path : path.replace(/\/+$/, "") || "/";
};

const startsWithSegment = (path: string, prefix: string) =>
  path === prefix || path.startsWith(`${prefix}/`);

// Keyword URLs (/sunglasses, /<brand>, /<product>) are resolved by the SEO
// resolver; its result for the same path decides their type.
export function posthogPageType(
  pathname: string,
  resolved?: ResolvedSeoRoute
): PostHogPageType {
  const path = normalizePath(pathname);
  if (path === "/") return "home";
  if (startsWithSegment(path, "/store/checkout/success")) return "success";
  if (startsWithSegment(path, "/store/checkout")) return "checkout";
  if (startsWithSegment(path, "/store/cart")) return "cart";
  if (path.startsWith("/store/product/")) return "product";
  if (path === "/brands" || startsWithSegment(path, "/store/manufacturer")) {
    return "brand";
  }
  if (
    path.startsWith("/store/category/") ||
    startsWithSegment(path, "/store/special") ||
    startsWithSegment(path, "/store/catalog")
  ) {
    return "category";
  }
  if (startsWithSegment(path, "/search") || path === "/store/search") {
    return "search";
  }
  if (startsWithSegment(path, "/account")) return "account";
  if (classifyNavigation({ pathname: path }) === "info") return "info";

  const resolvedPath =
    resolved && typeof resolved.path === "string" && resolved.path
      ? normalizePath(resolved.path)
      : "";
  const type =
    resolved &&
    resolved.notFound !== true &&
    (!resolvedPath || resolvedPath === path)
      ? String(resolved.type || "")
      : "";
  if (type === "product") return "product";
  if (type === "category") return "category";
  if (type === "manufacturer" || type === "brand") return "brand";
  if (type === "page" || type === "information") return "info";
  if (path === "/sunglasses" || path === "/eyeglasses") return "category";
  return "other";
}

const initialResolvedRoute = (): ResolvedSeoRoute =>
  (
    window as unknown as {
      __syncState__?: {
        vuex?: { vuefront?: { resolvedSeoRoute?: ResolvedSeoRoute } };
      };
    }
  ).__syncState__?.vuex?.vuefront?.resolvedSeoRoute || null;

let pageTypeResolver: ((pathname: string) => PostHogPageType) | null = null;

// entry-client.js registers the store-aware resolver once the app exists, so
// a late Analytics grant on an SPA page reports that page's type.
export function setPostHogPageTypeResolver(
  resolver: ((pathname: string) => PostHogPageType) | null
): void {
  pageTypeResolver = resolver;
}

const currentPageType = (pathname: string): PostHogPageType => {
  try {
    if (pageTypeResolver) return pageTypeResolver(pathname);
  } catch {
    /* Fall back to the server-resolved route below. */
  }
  return posthogPageType(pathname, initialResolvedRoute());
};

// ── Gate ────────────────────────────────────────────────────────────────────

const metaContent = (name: string): string => {
  try {
    return (
      document
        .querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
        ?.content.trim() || ""
    );
  } catch {
    return "";
  }
};

export type PostHogSiteSettings = { token: string; hosts: string[] };

export const readPostHogSettings = (): PostHogSiteSettings => ({
  token: metaContent("vf:posthog-token"),
  hosts: metaContent("vf:posthog-hosts")
    .split(",")
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean),
});

// Everything except consent: build, token, host and automation. No fallback
// token or host exists, so dev, staging, stands and localhost stay silent.
export const posthogSiteAllowed = (
  production: boolean,
  settings: PostHogSiteSettings
): boolean =>
  production &&
  POSTHOG_TOKEN_PATTERN.test(settings.token) &&
  settings.hosts.includes(
    String(window.location?.hostname || "").toLowerCase()
  ) &&
  !isAutomatedBrowser(navigator);

// Analytics consent, and never with Global Privacy Control (owner D3).
export const posthogConsentAllowed = (): boolean => {
  const consent = window.vfConsent;
  if (!consent) return false;
  try {
    return consent.has("analytics") === true && consent.isGpcEnabled() !== true;
  } catch {
    return false;
  }
};

// ── Loader ──────────────────────────────────────────────────────────────────

export function installPostHog(options: PostHogLoaderOptions = {}): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.__vfPhLoaderInstalled) return;
  window.__vfPhLoaderInstalled = true;

  const discardQueue = () => {
    window.__vfPhDisabled = true;
    window.__vfPhQueue = [];
  };

  try {
    const production = options.production ?? import.meta.env.PROD === true;
    const settings = readPostHogSettings();
    if (!posthogSiteAllowed(production, settings)) {
      discardQueue();
      return;
    }

    const snapshot = (campaign: boolean): PostHogLanding => ({
      url: window.location.href,
      pathname: window.location.pathname,
      title: document.title,
      pageType: currentPageType(window.location.pathname),
      campaign,
    });
    // Captured now: the visitor may navigate before PostHog starts, and the
    // landing URL carries the campaign parameters. A page viewed while
    // Analytics was denied is never reported: after a late grant the first
    // view is the page the visitor is on at the grant.
    let landing: PostHogLanding | null = posthogConsentAllowed()
      ? snapshot(true)
      : null;
    const loadSdk = options.loadSdk ?? loadSlimSdk;
    const loadRuntime = options.loadRuntime ?? loadPostHogRuntime;
    let runtime: PostHogRuntime | null = null;
    let loading = false;
    let stopped = false;

    const optOut = () => {
      stopped = true;
      discardQueue();
      runtime?.shutdown();
    };

    const start = (sdk: PostHogModule, mod: PostHogRuntimeModule) => {
      runtime = mod.startPostHog(sdk.default, {
        token: settings.token,
        landing: landing || snapshot(false),
        consentAllowed: posthogConsentAllowed,
        stop: optOut,
      });
      // An opt-out while the runtime started (it calls stop() itself).
      if (stopped) runtime.shutdown();
    };

    const load = () => {
      if (runtime || stopped) return;
      if (!posthogConsentAllowed()) {
        discardQueue();
        return;
      }
      if (typeof window.vfAfterStorefrontReady !== "function") {
        discardQueue();
        return;
      }
      if (!landing) landing = snapshot(false);
      // Queue again, also after a revoke and re-grant while still loading.
      window.__vfPhDisabled = false;
      if (loading) return;
      loading = true;
      window.vfAfterStorefrontReady(() => {
        if (stopped || !posthogConsentAllowed()) {
          loading = false;
          discardQueue();
          return;
        }
        // One load step: the SDK and the runtime, or neither. Any failure
        // (also a synchronous one) leaves PostHog off for this page.
        Promise.resolve()
          .then(() => Promise.all([loadSdk(), loadRuntime()]))
          .then(([sdk, mod]) => {
            loading = false;
            // Consent can change while the chunks download.
            if (runtime || stopped || !posthogConsentAllowed()) {
              if (!runtime) discardQueue();
              return;
            }
            start(sdk, mod);
          })
          .catch(() => {
            loading = false;
            if (!runtime) discardQueue();
          });
      });
    };

    const onDenied = () => {
      // Synchronous: CookieConsent reloads the page right after a revoke.
      if (runtime) optOut();
      else discardQueue();
    };

    window.addEventListener("vf:consent-updated", () => {
      try {
        if (!posthogConsentAllowed()) {
          onDenied();
          return;
        }
        if (!runtime && !stopped) load();
      } catch {
        /* Analytics must never interrupt the storefront. */
      }
    });

    // A withdrawal in another tab changes the shared consent record; this
    // tab learns it only from the storage event (key null: storage cleared).
    window.addEventListener("storage", (event: StorageEvent) => {
      try {
        if (event.key !== CONSENT_STORAGE_KEY && event.key !== null) return;
        if (!posthogConsentAllowed()) onDenied();
      } catch {
        /* Analytics must never interrupt the storefront. */
      }
    });

    load();
  } catch {
    discardQueue();
  }
}
